import React, { useCallback, useEffect, useRef, useState } from 'react';
import DsModal from '../../../common/DsModal';
import ChatLinkPreviewCard from '../timeline/ChatLinkPreviewCard';
import '../../../../css/common/chat/ChatAssetModal.css';

const PAGE = 30;

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
  } catch {
    return '';
  }
};

const toHref = (url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

// loadLinks(offset, limit) => { links, hasMore } を注入。event/personal/group いずれのチャットでも再利用。
export default function ChatLinksModal({ isOpen, onClose, loadLinks }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState('');
  const offsetRef = useRef(0);
  const loadedRef = useRef(false);

  const load = useCallback(async (reset) => {
    if (typeof loadLinks !== 'function') return;
    setLoading(true);
    setError('');
    try {
      const off = reset ? 0 : offsetRef.current;
      const data = await loadLinks(off, PAGE);
      const next = Array.isArray(data?.links) ? data.links : [];
      setLinks((prev) => (reset ? next : [...prev, ...next]));
      offsetRef.current = off + PAGE;
      setHasMore(Boolean(data?.hasMore));
    } catch {
      setError('リンクの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, [loadLinks]);

  useEffect(() => {
    if (isOpen && !loadedRef.current) {
      loadedRef.current = true;
      offsetRef.current = 0;
      setLinks([]);
      load(true);
    }
    if (!isOpen) loadedRef.current = false;
  }, [isOpen, load]);

  if (!isOpen) return null;

  return (
    <DsModal open={isOpen} onClose={onClose} title="リンク" size="lg" className="chat-asset-modal chat-links-modal">
      <div className="chat-asset-modal__note">
        このチャットで送られたリンク（直近1年）。これより前はチャット検索をご利用ください。
      </div>
      {error ? <div className="chat-asset-modal__error">{error}</div> : null}
      {!loading && links.length === 0 && !error ? (
        <div className="chat-asset-modal__empty">リンクはまだありません。</div>
      ) : null}
      <div className="chat-links-modal__list">
        {links.map((item) => {
          const preview = {
            ...(item.linkPreview || {}),
            url: item.linkPreview?.url || toHref(item.url),
            kind: item.linkPreview?.kind || 'link',
            status: item.linkPreview?.status || 'pending',
          };
          return (
            <div className="chat-links-modal__item" key={`${item.messageId}:${item.url}`}>
              <div className="chat-links-modal__date">
                {formatDate(item.createdAt)}
                {item.senderName ? ` ・ ${item.senderName}` : ''}
              </div>
              <ChatLinkPreviewCard preview={preview} autoFollow={false} displayFallback />
            </div>
          );
        })}
      </div>
      {loading ? <div className="chat-asset-modal__loading">読み込み中…</div> : null}
      {hasMore && !loading ? (
        <button type="button" className="chat-asset-modal__more" onClick={() => load(false)}>
          もっと見る
        </button>
      ) : null}
    </DsModal>
  );
}
